import _ from 'lodash';
import Vue from 'vue';
import SignupApi from '../../api/SignupApi';
import LoginApi from '../../api/LoginApi';
import phoneNumber from '../../utils/phoneNumber';
import Segment from '../../Segment';

const signupFlowSteps = {
  enterDetails: 'enter-details',
  enterCode: 'enter-code',
  whatsappOptin: 'whatsapp-optin',
};

const errorTypes = {
  memberAlreadyExists: 'MEMBER_ALREADY_EXISTS',
  cookiesDisabled: 'COOKIES_DISABLED',
};

const state = {
  currentFlowStep: null,
  utmParams: {},
  phoneNumber: '',
  firstName: '',
  lastName: '',
  code: '',
  createdCustomerId: null,
  signupStage: null,
  email: '',
  enableMarketingEmails: '',
  serverErrors: {
    generateCode: '',
    createCustomer: '',
    authenticate: '',
    sendBotiIntro: '',
  },
};

const getters = {
  signupFlowSteps: () => signupFlowSteps,
  phoneNumber: state => state.phoneNumber.replace(/\D/g, ''),
  amplitudeUtmParams: state => _transformUtmForAmplitude(state.utmParams),
  errorTypes: () => errorTypes,
};

const actions = {
  setUtmParams({ commit }, utmParams) {
    commit('setUtmParams', { utmParams });
  },
  async setCode({ commit }, code) {
    commit('setCode', { code });
  },
  async generateCode({ state, dispatch, commit }) {
    if (phoneNumber.validatePhoneNumber(state.phoneNumber)) {
      try {
        await LoginApi.generateCode({ phoneNumber: state.phoneNumber });
      } catch (e) {
        dispatch('catchServerError', { e, functionName: 'generateCode' });
      }
    } else {
      commit('setError', { error: 'INVALID_PHONE_NUMBER', functionName: 'generateCode' });
      throw new Error('Invalid phone number');
    }
  },
  async createCustomer({ state, dispatch, commit }) {
    if (!state.utmParams.utm_term) {
      const referrerEoy = localStorage.getItem('riseup.utm_referrer_eoy');
      if (referrerEoy) {
        commit('setUtmParams', { utmParams: { ...state.utmParams, utm_term: referrerEoy } });
      }
    }
    try {
      const resp = await SignupApi.createCustomer({
        firstName: state.firstName,
        lastName: state.lastName,
        phoneNumber: state.phoneNumber,
        email: state.email,
        enableMarketingEmails: state.enableMarketingEmails,
        code: state.code,
        utmParams: state.utmParams,
      });

      const { customerId } = resp.data;
      const answers = { ...this.state.leadQualification.answers };
      if (customerId && !_.isEmpty(answers)) {
        Segment.trackUserInteraction('LQAnswers', answers);
        await SignupApi.saveCustomerAnswers(answers);
      }
      commit('setCreatedCustomerId', customerId);
    } catch (e) {
      dispatch('catchServerError', { e, functionName: 'createCustomer' });
    }
  },
  async authenticate({ commit, state, dispatch }, code) {
    commit('setCode', { code });
    try {
      await LoginApi.authenticate({ phoneNumber: state.phoneNumber, code });
    } catch (e) {
      dispatch('catchServerError', { e, functionName: 'authenticate' });
    }
  },
  async sendBotiIntro({ dispatch }) {
    try {
      await SignupApi.sendBotiIntro();
    } catch (e) {
      dispatch('catchServerError', { e, functionName: 'sendBotiIntro' });
    }
  },
  catchServerError({ commit, state }, { e, functionName }) {
    if (e.response && e.response.status === 400) {
      commit('setError', { error: e.response.data, functionName });
    } else if (e.response && e.response.status === 409) {
      commit('setError', { error: errorTypes.cookiesDisabled, functionName });
    } else {
      commit('setError', { error: 'UNKNOWN_ERROR', functionName });
    }
    throw e;
  },
};

const mutations = {
  setUtmParams(state, { utmParams }) {
    state.utmParams = utmParams;
  },
  setPhoneNumber(state, phoneNumber) {
    state.phoneNumber = phoneNumber;
  },
  setFirstName(state, firstName) {
    state.firstName = firstName;
  },
  setLastName(state, lastName) {
    state.lastName = lastName;
  },
  setEmail(state, email) {
    state.email = email; // eslint-disable-line
  },
  setEnableMarketingEmails(state, enableMarketingEmails) {
    state.enableMarketingEmails = enableMarketingEmails; // eslint-disable-line
  },
  setCode(state, { code }) {
    state.code = code;
  },
  setError(state, { error, functionName }) {
    Vue.set(state.serverErrors, functionName, error);
  },
  resetError(state, functionName) {
    Vue.set(state.serverErrors, functionName, '');
  },
  setCurrentFlowStep(state, currentFlowStep) {
    state.currentFlowStep = currentFlowStep;
  },
  setCreatedCustomerId(state, customerId) {
    state.createdCustomerId = customerId;
  },
};

// Amplitude overrides some of the utm params, and it doesn't matter that we do the identify call, so we override the key names specifically for that
// https://community.segment.com/t/637f2h/initial_utm-gets-overwritten
function _transformUtmForAmplitude(utmParams) {
  return _.mapKeys(utmParams, (v, k) => `signup_${k}`);
}

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
};
